/**
 * InputRequestForm (PRD-005 / ARCH-028, Batch IR-2a, IR-T-07).
 *
 * The ONE shared recipient form rendered across all three surfaces (OS owner
 * card, task card, general card) and reused read-only by the operator surface
 * (Batch IR-2b). It renders an `input_requests` row's questions by kind,
 * validates client-side (mirroring the server RPC), submits once via
 * `submit_input_response` (the only write path; no recipient table grant), and
 * on success flips to the read-only answered view.
 *
 * Two public components are exported:
 *   - <InputRequestForm> — the open/editable form. Shows field errors, submits,
 *     and renders <AnswerReadback> for each answer once submitted.
 *   - <AnswerReadback> — the read-only answered view of one request. Used by the
 *     submitted state here AND directly by the operator read surface (IR-2b),
 *     so it is exported standalone.
 *
 * PRIMITIVES (ARCH-008): text kinds use the shadcn Input / Textarea / Label /
 * Button primitives from @/components/ui. The two choice kinds use the repo's
 * established native-radio/checkbox-in-label idiom (CoachingDepthSelector,
 * settings.tsx) styled with the design tokens: there is no shadcn RadioGroup /
 * Checkbox primitive in components/ui, and the form may not add the Radix
 * dependency (package.json is out of this batch's file ownership). The
 * enforced ARCH-008 archgate rules (no @radix-ui/react-toast, no vaul) are
 * satisfied. See IMPL-NOTES-IR.md.
 *
 * A11Y (ARCH-026): every field has a <Label htmlFor> (choice groups use a
 * <fieldset>/<legend>); required state is signaled by a text "Required"/
 * "Optional" marker (not color alone); errors render in text beside the field
 * with role="alert"; aria-invalid / aria-describedby / aria-required are wired;
 * focus is visible via the shadcn focus-visible ring and an explicit focus ring
 * on the choice rows.
 *
 * COPY: all chrome strings are verbatim from COPY-input-requests.md §B/§C. The
 * caller passes only the operator-authored content (title / intro / questions /
 * answers) on the row; this component owns the fixed chrome.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  validateInputAnswers,
  type InputRequestAnswer,
  type InputRequestAnswers,
  type InputRequestQuestion,
  type InputRequestQuestions,
} from '@studio/api-schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  submitInputResponse,
  type RecipientInputRequest,
} from './lib';

// ---- Locked copy (COPY §B/§C). Identical across recipient surfaces. ---------

const FIELD_PLACEHOLDER = 'Type your answer';
const ERROR_TEXT = 'This one is needed before you send.';
const ERROR_SINGLE = 'Please pick one to continue.';
const ERROR_MULTI = 'Please pick at least one to continue.';
const SUBMIT_LABEL = 'Send to Eric';
const LEFT_BLANK = 'Left blank';

/** Submit helper line. Section B and C use slightly different no-error text. */
export type SubmitHelperRegister = 'os' | 'portal';
const HELPER_NO_ERRORS: Record<SubmitHelperRegister, string> = {
  os: 'You can answer in any order. It saves the moment you send.',
  portal: 'Your answers are delivered straight to Eric.',
};

/** Per-kind error string (COPY §B field errors). */
function errorFor(kind: InputRequestQuestion['kind']): string {
  if (kind === 'single_choice') return ERROR_SINGLE;
  if (kind === 'multi_choice') return ERROR_MULTI;
  return ERROR_TEXT;
}

/** The empty value for a kind: [] for multi_choice, '' for everything else. */
function emptyValue(kind: InputRequestQuestion['kind']): InputRequestAnswer {
  return kind === 'multi_choice' ? [] : '';
}

/** True when an answer value counts as "present" for the required check. */
function isAnswered(value: InputRequestAnswer): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return value.trim().length > 0;
}

// ---- One question, open / editable ------------------------------------------

function QuestionField({
  question,
  index,
  value,
  onChange,
  error,
}: {
  question: InputRequestQuestion;
  index: number;
  value: InputRequestAnswer;
  onChange: (next: InputRequestAnswer) => void;
  error: string | null;
}): JSX.Element {
  const fieldId = `ir-${question.id}`;
  const errorId = `ir-${question.id}-err`;
  const describedBy = error ? errorId : undefined;
  const requiredMarker = question.required ? 'Required' : 'Optional';
  const stringValue = typeof value === 'string' ? value : '';
  const arrayValue = Array.isArray(value) ? value : [];

  const promptRow = (
    <div className="mb-1 flex flex-wrap items-baseline gap-2.5">
      <span
        className="font-display text-lg italic leading-none text-[color:var(--color-gray-300)]"
        aria-hidden="true"
      >
        {index + 1}
      </span>
      <Label
        htmlFor={fieldId}
        className="text-[15px] font-semibold leading-snug text-[color:var(--color-black)]"
      >
        {question.prompt}
      </Label>
      <span
        className={[
          'text-[11px] font-medium',
          question.required
            ? 'text-[color:var(--color-rose-dark)]'
            : 'text-[color:var(--color-gray-300)]',
        ].join(' ')}
      >
        {requiredMarker}
      </span>
    </div>
  );

  const hintRow = question.hint ? (
    <p className="mb-2.5 text-[13px] text-[color:var(--color-text-mute)]">
      {question.hint}
    </p>
  ) : null;

  const errorRow = error ? (
    <p
      id={errorId}
      role="alert"
      className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-[color:var(--color-terracotta)]"
    >
      <span aria-hidden="true">&#9651;</span>
      {error}
    </p>
  ) : null;

  if (question.kind === 'long_text') {
    return (
      <div>
        {promptRow}
        {hintRow}
        <Textarea
          id={fieldId}
          rows={3}
          value={stringValue}
          placeholder={FIELD_PLACEHOLDER}
          aria-required={question.required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          className={
            error
              ? 'border-[color:var(--color-terracotta)] focus-visible:ring-[color:var(--color-terracotta)]'
              : undefined
          }
        />
        {errorRow}
      </div>
    );
  }

  if (question.kind === 'short_text') {
    return (
      <div>
        {promptRow}
        {hintRow}
        <Input
          id={fieldId}
          value={stringValue}
          placeholder={FIELD_PLACEHOLDER}
          aria-required={question.required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          className={
            error
              ? 'border-[color:var(--color-terracotta)] focus-visible:ring-[color:var(--color-terracotta)]'
              : undefined
          }
        />
        {errorRow}
      </div>
    );
  }

  // single_choice / multi_choice: native radio/checkbox-in-label (the repo
  // idiom; ARCH-008 enforced rules satisfied). The group is a fieldset so the
  // prompt is the accessible group label; the visible numbered prompt above is
  // aria-hidden-decorated and the legend carries the same text for AT.
  const options = question.options ?? [];
  const isMulti = question.kind === 'multi_choice';

  return (
    <fieldset
      aria-required={question.required}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
    >
      <legend className="sr-only">{question.prompt}</legend>
      {promptRow}
      {hintRow}
      <div id={fieldId} className="flex flex-col gap-2">
        {options.map((option) => {
          const selected = isMulti
            ? arrayValue.includes(option)
            : stringValue === option;
          return (
            <label
              key={option}
              className={[
                'flex cursor-pointer items-center gap-3 rounded-md border-[1.5px] px-[15px] py-3 text-[15px] transition-colors',
                'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[color:var(--color-navy)]',
                selected
                  ? 'border-[color:var(--color-navy)] bg-[color:var(--color-rose-subtle2)]'
                  : error
                    ? 'border-[color:var(--color-terracotta)] bg-[color:var(--color-white)]'
                    : 'border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] hover:bg-[color:var(--color-linen)]',
              ].join(' ')}
            >
              <input
                type={isMulti ? 'checkbox' : 'radio'}
                name={fieldId}
                value={option}
                checked={selected}
                onChange={() => {
                  if (isMulti) {
                    onChange(
                      selected
                        ? arrayValue.filter((o) => o !== option)
                        : [...arrayValue, option],
                    );
                  } else {
                    onChange(option);
                  }
                }}
                className="h-4 w-4 shrink-0 accent-[color:var(--color-navy)]"
              />
              <span
                className={[
                  selected ? 'font-medium' : 'font-normal',
                  'text-[color:var(--color-black)]',
                ].join(' ')}
              >
                {option}
              </span>
            </label>
          );
        })}
      </div>
      {errorRow}
    </fieldset>
  );
}

// ---- One answer, read-only --------------------------------------------------

/**
 * The read-only view of one answered question (COPY §B/§C). An unanswered
 * optional question reads `Left blank`. Exported so the operator read surface
 * (IR-2b) can render answers without re-implementing the readback.
 */
export function AnswerReadback({
  question,
  index,
  value,
}: {
  question: InputRequestQuestion;
  index: number;
  value: InputRequestAnswer | undefined;
}): JSX.Element {
  const has =
    value !== undefined &&
    (Array.isArray(value) ? value.length > 0 : value.trim().length > 0);
  const display = Array.isArray(value) ? value.join(' · ') : (value ?? '');

  return (
    <div>
      <div className="mb-1.5 flex items-baseline gap-2.5">
        <span
          className="font-display text-lg italic leading-none text-[color:var(--color-gray-300)]"
          aria-hidden="true"
        >
          {index + 1}
        </span>
        <p className="text-sm font-semibold leading-snug text-[color:var(--color-black)]">
          {question.prompt}
        </p>
      </div>
      {has ? (
        <p className="ml-[26px] border-l-2 border-[color:var(--color-line-dark)] pl-3.5 text-[15px] leading-relaxed text-[color:var(--color-black)] text-pretty">
          {display}
        </p>
      ) : (
        <p className="ml-[26px] text-sm italic text-[color:var(--color-gray-300)]">
          {LEFT_BLANK}
        </p>
      )}
    </div>
  );
}

/**
 * The full read-only answered view of a request: each answer under its prompt.
 * Reads `answers` off the request row (recorded by the RPC). Exported for the
 * submitted state here and the operator read surface (IR-2b).
 */
export function AnsweredView({
  request,
}: {
  request: RecipientInputRequest;
}): JSX.Element {
  const ordered = useMemo(
    () => [...request.questions].sort((a, b) => a.order - b.order),
    [request.questions],
  );
  return (
    <div className="flex flex-col gap-[clamp(22px,3vw,28px)]">
      {ordered.map((question, index) => (
        <AnswerReadback
          key={question.id}
          question={question}
          index={index}
          value={request.answers[question.id]}
        />
      ))}
    </div>
  );
}

// ---- The open / editable form -----------------------------------------------

/**
 * The open form. Renders the questions, validates on submit (mirroring the RPC
 * via validateInputAnswers from @studio/api-schemas), and submits once. On
 * success it calls `onSubmitted` so the surface can flip to its submitted
 * chrome; it does NOT render the answered view itself (the surface owns the
 * submitted banner around <AnsweredView>). While in-flight the submit button is
 * disabled.
 */
export function InputRequestForm({
  request,
  helperRegister = 'portal',
  onSubmitted,
}: {
  request: RecipientInputRequest;
  helperRegister?: SubmitHelperRegister;
  onSubmitted: () => void;
}): JSX.Element {
  const ordered = useMemo(
    () => [...request.questions].sort((a, b) => a.order - b.order),
    [request.questions],
  );

  const [answers, setAnswers] = useState<InputRequestAnswers>(() =>
    Object.fromEntries(ordered.map((q) => [q.id, emptyValue(q.kind)])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setAnswer = useCallback(
    (id: string, next: InputRequestAnswer) => {
      setAnswers((prev) => ({ ...prev, [id]: next }));
      setErrors((prev) => {
        if (!prev[id]) return prev;
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    },
    [],
  );

  const buildErrors = useCallback(
    (questions: InputRequestQuestions, current: InputRequestAnswers) => {
      const next: Record<string, string> = {};
      // Mirror the RPC: required must be present (multi needs >=1), and any
      // chosen option must be valid. validateInputAnswers is the shared mirror;
      // we map its problems to the per-kind COPY error strings.
      const problems = validateInputAnswers(questions, current);
      for (const q of questions) {
        const hasProblem = problems.some((p) => p.question_id === q.id);
        if (q.required && !isAnswered(current[q.id] ?? emptyValue(q.kind))) {
          next[q.id] = errorFor(q.kind);
        } else if (hasProblem) {
          next[q.id] = errorFor(q.kind);
        }
      }
      return next;
    },
    [],
  );

  const onSubmit = useCallback(async () => {
    setFormError(null);
    const next = buildErrors(ordered, answers);
    setErrors(next);
    const firstBad = ordered.find((q) => next[q.id]);
    if (firstBad) {
      const el = document.getElementById(`ir-${firstBad.id}`);
      if (el instanceof HTMLElement) el.focus();
      return;
    }
    setSubmitting(true);
    const result = await submitInputResponse(request.id, answers);
    setSubmitting(false);
    if (result.ok) {
      onSubmitted();
    } else {
      setFormError(result.message);
    }
  }, [answers, buildErrors, onSubmitted, ordered, request.id]);

  const missing = Object.keys(errors).length;
  const helper =
    missing > 0
      ? `${missing} ${missing === 1 ? 'answer is' : 'answers are'} still needed${helperRegister === 'os' ? ' above' : ''}.`
      : HELPER_NO_ERRORS[helperRegister];

  return (
    <div>
      <div className="flex flex-col gap-[clamp(20px,3vw,24px)]">
        {ordered.map((question, index) => (
          <QuestionField
            key={question.id}
            question={question}
            index={index}
            value={answers[question.id] ?? emptyValue(question.kind)}
            error={errors[question.id] ?? null}
            onChange={(next) => setAnswer(question.id, next)}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button
          type="button"
          onClick={() => void onSubmit()}
          disabled={submitting}
        >
          {submitting ? 'Sending...' : SUBMIT_LABEL}
        </Button>
        <span
          className={[
            'max-w-[300px] text-[13px] text-pretty',
            missing > 0
              ? 'text-[color:var(--color-terracotta)]'
              : 'text-[color:var(--color-text-mute)]',
          ].join(' ')}
        >
          {helper}
        </span>
      </div>

      {formError ? (
        <p
          role="alert"
          className="mt-3 text-[13px] font-medium text-[color:var(--color-terracotta)]"
        >
          {formError}
        </p>
      ) : null}
    </div>
  );
}
